/**
 * All cross-page state, mirroring the app's `BookingContext`.
 *
 * Two differences from the phone, both deliberate:
 *
 * 1. **The draft offering survives a reload.** On a phone the flow is three
 *    taps in one sitting; on the web a donor opens the checkout in a new tab,
 *    goes to look up how to spell a name in Tibetan, and comes back. Losing a
 *    half-written dedication to a refresh would be the site's worst bug, so the
 *    draft is persisted to sessionStorage — session, not local, because a draft
 *    is not something to find waiting a week later.
 *
 * 2. **Saved monasteries round-trip to the server.** The app keeps them in
 *    memory and loses them on restart. Here they are held under the donor key
 *    so they survive, with local state updated first so the heart responds
 *    immediately and reconciles after.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { trackEvent } from '../lib/consent';

const BookingContext = createContext(null);

const DRAFT_KEY = 'gompa.draft';
const LINEAGE_KEY = 'gompa.lineages';

const EMPTY_DRAFT = {
  monastery: null,
  service: null,
  option: null,
  dedicationText: '',
  tibetanDedicationText: '',
  donorName: '',
  email: '',
  paymentMethod: 'card',
  shareDedication: false,
  extraSupportCents: 0,
};

const readJson = (storage, key, fallback) => {
  try {
    const raw = storage.getItem(key);
    return raw ? { ...fallback, ...JSON.parse(raw) } : fallback;
  } catch {
    return fallback;
  }
};

export function BookingProvider({ children }) {
  const [draft, setDraft] = useState(() =>
    typeof sessionStorage === 'undefined' ? EMPTY_DRAFT : readJson(sessionStorage, DRAFT_KEY, EMPTY_DRAFT)
  );
  const [savedIds, setSavedIds] = useState([]);
  const [lastOrder, setLastOrder] = useState(null);

  /**
   * Lineage filter preferences. Kept in localStorage rather than on the server:
   * it is a display preference, and a donor should not have to have anything
   * resembling an account to filter a list.
   *
   * `{}` means no filter at all. An empty object must never be read as "hide
   * everything" — a monastery the user cannot bring back is a bug they cannot
   * diagnose.
   */
  const [lineagePreferences, setLineagePreferences] = useState(() => {
    if (typeof localStorage === 'undefined') return {};
    return readJson(localStorage, LINEAGE_KEY, {});
  });

  useEffect(() => {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  }, [draft]);

  useEffect(() => {
    localStorage.setItem(LINEAGE_KEY, JSON.stringify(lineagePreferences));
  }, [lineagePreferences]);

  useEffect(() => {
    let cancelled = false;
    api
      .saved()
      .then(({ ids }) => !cancelled && setSavedIds(ids))
      // A saved list that fails to load is not worth an error screen; the
      // hearts simply start empty and the next toggle re-syncs them.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const updateDraft = useCallback((patch) => setDraft((prev) => ({ ...prev, ...patch })), []);
  const clearDraft = useCallback(() => setDraft(EMPTY_DRAFT), []);

  const isSaved = useCallback((id) => savedIds.includes(id), [savedIds]);

  const toggleSaved = useCallback(
    (id) => {
      // Direction is computed from the *current* state and the tracking call
      // fires here, outside the updater below — a functional setState updater
      // can run more than once for a single call (React 18 StrictMode
      // double-invokes it in development specifically to catch impure
      // updaters), and a side effect living inside one fires exactly as many
      // times as the updater does. Putting the event here means it always
      // fires once per real toggle, dev or production.
      const wasSaved = savedIds.includes(id);
      trackEvent('toggle_saved', { item_id: id, direction: wasSaved ? 'unsave' : 'save' });
      setSavedIds((prev) => {
        const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
        // Optimistic: the heart fills on the click, not on the round trip. If
        // the write fails the server keeps the old list and the next load
        // corrects it — a lost bookmark is a smaller harm than a control that
        // lags behind the finger.
        api.setSaved(next).catch(() => {});
        return next;
      });
    },
    [savedIds]
  );

  const toggleLineage = useCallback((key) => {
    const adding = !lineagePreferences[key];
    trackEvent('filter_lineage', { lineage: key, direction: adding ? 'add' : 'remove' });
    setLineagePreferences((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = true;
      return next;
    });
  }, [lineagePreferences]);

  const clearLineages = useCallback(() => setLineagePreferences({}), []);

  const activeLineages = useMemo(
    () => Object.keys(lineagePreferences).filter((k) => lineagePreferences[k]),
    [lineagePreferences]
  );

  const value = useMemo(
    () => ({
      draft,
      updateDraft,
      clearDraft,
      savedIds,
      isSaved,
      toggleSaved,
      lineagePreferences,
      activeLineages,
      toggleLineage,
      clearLineages,
      lastOrder,
      setLastOrder,
    }),
    [
      draft, updateDraft, clearDraft, savedIds, isSaved, toggleSaved,
      lineagePreferences, activeLineages, toggleLineage, clearLineages, lastOrder,
    ]
  );

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
}

export function useBooking() {
  const context = useContext(BookingContext);
  if (!context) throw new Error('useBooking must be used inside a BookingProvider');
  return context;
}
